import {Editor, Element, Node, Path, Range, Transforms} from "slate";

import {
    extractIndentDepth,
    markdownChunkToSlateBlock,
} from "../markdown-to-slate.ts";
import {text} from "../node-builders.ts";
import {editorToMarkdownComponents} from "../slate-to-markdown.ts";

import {getParentBlock} from "./block-helpers.ts";
import {clearEditor} from "./editor-helpers.ts";
import {increaseListDepth, isListNode} from "./list-helpers.ts";
import {getSkeletonPathByPlaceholderKey} from "./skeleton-helpers.ts";
import {isSelectionWithinTable} from "./table-helpers.ts";

// We use these symbol keys to store some data on the active editor instance:
export const lastSelectionKey: symbol = Symbol.for("coeditorLastSelectionKey");
const unsavedChangesKey: symbol = Symbol.for("coeditorUnsavedChangesKey");
const initialEditCompletedKey: symbol = Symbol.for(
    "coeditorInitialEditCompletedKey",
);
export const khanmigoEditStartKey: symbol = Symbol.for("khanmigoEditingKey");

/**
 * Inserts an array of chunks into the editor. We assume that this is the
 * first insertion of any content into the editor, since the backend starts
 * by sending all the template chunks with placeholders, so we clear out the
 * editor here and insert these chunks one-by-one.
 */
export const insertTemplateChunks = (editor: Editor, chunks: string[]) => {
    // Clear all content from the editor to start!
    clearEditor(editor);

    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        if (chunk.trim() === "") {
            continue;
        }

        const newBlock = markdownChunkToSlateBlock(chunk);

        Editor.withoutNormalizing(editor, () => {
            let at;

            if (i === 0) {
                // An empty editor technically must have one empty paragraph
                // node in it for Slate to work, so for the very first chunk
                // we remove that empty block and insert the new one in a
                // "transaction" (without normalizing).
                Transforms.removeNodes(editor, {at: [0]});
            }

            const parent = getParentBlock(editor);
            if (parent) {
                // If there's a parent block, we'll want to insert this new node
                // at the next sibling path after that parent's path:
                at = Path.next(parent[1]);
            }

            Transforms.insertNodes(editor, newBlock, {at});

            if (at) {
                // Make sure selection moves to the end of the block we just
                // inserted, so that the next chunk gets put in the right place:
                Transforms.select(editor, Editor.end(editor, at));
            }
        });

        if (isListNode(newBlock)) {
            // If this was a list node, we need to check to see if the node was
            // indented (had spaces before the leading "- " or "1. "). If so, we
            // need to increase its depth (make it into a nested list). Since
            // normalization has already run at this point, and we've moved
            // selection to the end of the recently-inserted list item, we can
            // call `increaseListDepth` to handle the tricky nesting work.
            const depth = extractIndentDepth(chunks[i]);
            if (depth > 0) {
                increaseListDepth(
                    editor,
                    editor.selection,
                    depth,
                    newBlock.type,
                );
            }
        }
    }
};

/**
 * Inserts a "chunk" of text into the editor at the last selection. A chunk
 * is roughly equivalent to a stringified Markdown block (e.g. a heading, a
 * paragraph, a code block), but individual list items are also sent from the
 * backend as their own chunks. This function handles converting the string
 * chunk into a Slate node and inserting at the right place in the editor.
 */
export const insertChunkAtSelection = (editor: Editor, chunk: string): void => {
    // Make sure that editor.selection is set somewhere reasonable. If it
    // isn't set, we try to restore the last selection. Failing that, put
    // selection at the end of the editor.
    if (!editor.selection) {
        if (editor[lastSelectionKey]) {
            // If the editor loses focus during an edit, then the editor
            // selection will be null:
            // https://github.com/ianstormtaylor/slate/issues/3412,
            // but we can use the last selection we had to insert text at the
            // right place.
            Transforms.select(editor, editor[lastSelectionKey]);
        } else {
            // If the editor has only just appeared and hasn't been focused yet,
            // selection will be null. In this case, set selection to be a cursor
            // at the very end of the document.
            const endPoint = Editor.end(editor, []);
            Transforms.select(editor, {anchor: endPoint, focus: endPoint});
        }
    }

    const newBlock = markdownChunkToSlateBlock(chunk);

    if (isSelectionWithinTable(editor) && isListNode(newBlock)) {
        // A special case: inside table cells, we don't support block elements
        // like lists (since Markdown doesn't support this). But our rubric
        // generator simulates lists with "- " prefixes and newlines. We want
        // to avoid trying to insert a list block into a table cell, since it
        // breaks the table layout, so we extract the text from the list item
        // and insert that instead.
        const listItem = newBlock.children[0];
        if (listItem) {
            const textChildren = listItem.children
                .filter(
                    (n) => Element.isElement(n) && n.type === "list-item-text",
                )
                .flatMap((n) => n.children);

            // We've parsed out the "- " prefix by now, so add it back:
            textChildren.unshift(text("- "));
            // And same with the trailing newline after the list item:
            textChildren.push(text("\n"));

            Transforms.insertNodes(editor, textChildren);
            return;
        }
    }

    if (
        newBlock.type === "paragraph" &&
        expandedRangeWithinSameBlock(editor.selection)
    ) {
        // If there is some selected text (the range is expanded), and the
        // selection is fully within one block node, AND the new block is a
        // paragraph (so it didn't have any special formatting prefixes like
        // "# "), we assume we're inserting inlines to replace the current
        // selection. Just insert newBlock.children instead of the whole
        // paragraph block.
        Transforms.insertNodes(editor, newBlock.children);
        saveCurrentSelection(editor);
        return;
    }

    if (Range.isCollapsed(editor.selection!)) {
        if (Node.string(getParentBlock(editor)[0]) === "") {
            // If the current block is empty, remove it. This accounts for
            // cases where, e.g., the user has started an empty paragraph and
            // asked Khanmigo to fill in some content. We don't want to leave
            // the empty paragraph laying around.
            Transforms.removeNodes(editor, {at: editor.selection!});
        }

        // Insert the new block at selection; no content to delete.
        Transforms.insertNodes(editor, newBlock);

        // Make sure selection moves to the end of the block we just
        // inserted, so that the next chunk gets put in the right place:
        Transforms.select(editor, Editor.end(editor, editor.selection!));
    } else {
        Editor.withoutNormalizing(editor, () => {
            // First delete the text contained in the selection:
            Transforms.delete(editor, {at: editor.selection!});

            if (Node.string(getParentBlock(editor)[0]) === "") {
                // If that deletion left the parent block empty, remove it:
                Transforms.removeNodes(editor, {at: editor.selection!});
            }

            // And then insert the new block at the selection:
            Transforms.insertNodes(editor, newBlock, {at: editor.selection!});
        });
    }

    saveCurrentSelection(editor);
};

/**
 * Inserts a "chunk" of text into the editor at the given placeholder in the
 * document. A chunk is defined above. When the backend begins filling out a
 * document for the first time, it sends a representation of the full template
 * with completion placeholders marked and tagged with placeholder keys. The
 * backend then begins generating completions for those placeholders and
 * streaming them back to the frontend, chunk-by-chunk. This function handles
 * receiving one of those chunks, turning it into a Slate node, and inserting
 * it at the right placeholder in the document.
 */
export const insertChunkAtPlaceholder = (
    editor: Editor,
    placeholderKey: string,
    chunk: string,
): void => {
    const path = getSkeletonPathByPlaceholderKey(editor, placeholderKey);
    if (!path) {
        return;
    }

    const newBlock = markdownChunkToSlateBlock(chunk);

    Transforms.insertNodes(editor, newBlock, {at: path});

    if (isListNode(newBlock)) {
        // If this was a list node, we need to check to see if the node was
        // indented (had spaces before the leading "- " or "1. "). If so, we
        // need to increase its depth (make it into a nested list). Since
        // normalization has already run at this point, we can call
        // `increaseListDepth` to handle the tricky nesting work.
        const depth = extractIndentDepth(chunk);
        if (depth > 0) {
            // Since we just inserted this list item at the old path of the
            // skeleton, we can use the point before the new path of the
            // skeleton to refer to the list item's new location. Note that
            // in the case of nested lists, it may have been normalized deep
            // into a nested list somewhere, so this is the easiest way to
            // find its path!
            const pointBefore = Editor.before(
                editor,
                // We call this _again_, instead of reusing path from above,
                // since the path to the skeleton changed after the insert:
                getSkeletonPathByPlaceholderKey(editor, placeholderKey)!,
            );

            increaseListDepth(editor, pointBefore, depth, newBlock.type);
        }
    }
};

/**
 * Saves the current `editor.selection` in a special property on the editor
 * instance, owned by us. We do this because Slate's internals sometimes lead
 * to `editor.selection` getting updated in unexpected ways, like when the
 * editor loses focus. By setting this key that we own and control ourselves,
 * we can restore selection to where it was previously in these edge cases.
 */
export const saveCurrentSelection = (editor: Editor | null) => {
    if (!editor) {
        return;
    }

    editor[lastSelectionKey] = editor.selection;
};

/**
 * Marks the beginning of a stream of edits by Khanmigo into the editor.
 * Mainly, we need to keep track of the start point of the edit, so that
 * we can highlight the inserted range in a decoration.
 */
export const startKhanmigoEditing = (editor: Editor): void => {
    let startPoint = editor.selection?.anchor;
    if (!startPoint) {
        startPoint = Editor.end(editor, []);
    }

    editor[khanmigoEditStartKey] = startPoint;
    editor[initialEditCompletedKey] = false;
};

/**
 * Marks the end of a stream of edits by Khanmigo. Cleans up the property we
 * set to mark the beginning of the edit stream, and collapses the selection
 * to be just a cursor at the end of the edit.
 */
export const stopKhanmigoEditing = (editor: Editor): void => {
    delete editor[khanmigoEditStartKey];

    if (editor[initialEditCompletedKey] === false) {
        const {prefix, selection, suffix} = editorToMarkdownComponents(editor);
        editor[initialEditCompletedKey] = prefix + selection + suffix;
    }

    Transforms.collapse(editor, {edge: "end"});
};

/**
 * Checks if Khanmigo is actively editing the given editor.
 */
export const isKhanmigoEditing = (editor: Editor): boolean => {
    return khanmigoEditStartKey in editor;
};

/**
 * Checks if the editor text has changed since Khanmigo finished its initial
 * population of the document.
 */
export const hasTextChangedSinceInitialEdit = (editor: Editor): boolean => {
    if (!editor[initialEditCompletedKey]) {
        return false;
    }

    const {prefix, selection, suffix} = editorToMarkdownComponents(editor);
    const oldText = (editor[initialEditCompletedKey] || "").trim();
    const newText = (prefix + selection + suffix).trim();

    return oldText !== newText;
};

/**
 * Marks an editor as having unsaved changes.
 */
export const markUnsavedChanges = (editor: Editor) => {
    editor[unsavedChangesKey] = true;
};

/**
 * Checks if editor has unsaved changes.
 */
export const hasUnsavedChanges = (editor: Editor): boolean => {
    return unsavedChangesKey in editor;
};

/**
 * Marks the editor as not dirty (not having unsaved changes).
 */
export const clearUnsavedChanges = (editor: Editor) => {
    delete editor[unsavedChangesKey];
};

const expandedRangeWithinSameBlock = (range: Range | null) => {
    if (!range || Range.isCollapsed(range)) {
        return false;
    }

    const {anchor, focus} = range;

    return Path.compare(anchor.path, focus.path) === 0;
};
